import React from "react";

import useSessionStateSync from "@hooks/useSessionStateSync";

import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Box, Flex } from "@chakra-ui/react";

import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";

import { getTrackingId } from "@utils/utils";

import { FooterActions, FooterActionsProps } from "@components/FooterActions";
import { Stepper } from "@components/Stepper";
import { Text } from "@components/Text";

interface PropTypes {
    title?: React.ReactNode;
    description?: React.ReactNode;
    footerActionsProps?: FooterActionsProps;
    customFooterZIndex?: number;
    containerWidth?: (string | number | null)[] | string | number;
    noticeElement?: React.ReactNode;
    children: React.ReactNode;
    headerBackButton?: boolean;
    gap?: number | string;
    step?: number;
    className?: string;
    isEnrol?: boolean;
}

const ContentLayout = ({
    title,
    description,
    footerActionsProps,
    customFooterZIndex,
    containerWidth,
    noticeElement,
    headerBackButton = false,
    gap,
    children,
    step,
    className,
    isEnrol
}: PropTypes) => {
    const [, sessionState] = useSessionStateSync<CheckoutStateInterface>();

    let hasBackButton = false;
    if (footerActionsProps) {
        if (
            footerActionsProps.hasBackButton === undefined ||
            footerActionsProps.hasBackButton
        )
            hasBackButton = true;
    }

    return (
        <>
            <Flex
                direction="column"
                gap={5}
                px={[3, null, 20]}
                className={className}
            >
                <Flex
                    direction="column"
                    align="center"
                    pt={12}
                    pb={15}
                    maxW="1280px"
                    mx="auto"
                    w={containerWidth ?? ["95%", null, "70%", "60%", "40%"]}
                    m="auto"
                >
                    {!sessionState.checkout?.checkoutPurchasingAddon &&
                        step && <Stepper step={step} isEnrol={isEnrol} />}

                    <Flex direction="column" gap={gap ?? 10} w="full">
                        {title && (
                            <Flex align="start" justify="center" gap={4}>
                                {(hasBackButton || headerBackButton) && (
                                    <Box
                                        className={ClassesEnum.Button}
                                        data-tracking-id={getTrackingId(
                                            footerActionsProps
                                                ?.btnClassHierarchy?.back ?? [
                                                ClassesEnum.Button
                                            ],
                                            footerActionsProps?.btnActions
                                                ?.back ?? "back"
                                        )}
                                        mr="auto"
                                        cursor="pointer"
                                        display={{
                                            base: "inline-block",
                                            lg: "none"
                                        }}
                                        onClick={footerActionsProps?.onGoBack}
                                        lineHeight="41px"
                                    >
                                        <FontAwesomeIcon
                                            icon={faChevronLeft}
                                            color="var(--euka-colors-gray-900)"
                                            fontSize={24}
                                            data-testid="header-back-icon"
                                        />
                                    </Box>
                                )}
                                <Flex direction="column" gap={4}>
                                    <Text
                                        m="auto"
                                        textAlign={["center", "center"]}
                                        type="heading"
                                    >
                                        {title}
                                    </Text>
                                    {description && (
                                        <Text textAlign="center" type="normal">
                                            {description}
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                        )}
                        {children}
                    </Flex>
                </Flex>
            </Flex>
            <Box
                position="sticky"
                bottom={0}
                bg="white"
                pt={4}
                pb={4}
                mb={-2}
                // w="100vw"
                px={[3, null, 20]}
                // position="relative"
                left="calc(-50vw + 50%)"
                mt="auto"
                {...(customFooterZIndex
                    ? {
                          zIndex: customFooterZIndex
                      }
                    : {})}
            >
                {noticeElement ? (
                    <Flex
                        justify="space-between"
                        alignItems={{ md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={8}
                    >
                        {noticeElement}
                        {footerActionsProps && (
                            <FooterActions {...footerActionsProps} />
                        )}
                    </Flex>
                ) : (
                    <>
                        {footerActionsProps && (
                            <FooterActions {...footerActionsProps} />
                        )}
                    </>
                )}
            </Box>
        </>
    );
};

export default ContentLayout;
